import React, { memo, useCallback } from 'react'

import { useLocation } from 'react-router-dom'
import { isEqual } from 'date-fns'
import { useTheme } from 'styled-components'

import { Loading } from '@components/Loading'
import { Separator } from '@components/Separator'
import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { useFindTasks } from '@hooks/tasks/find'

import { LoaderContainer, NoTasksText } from './styles'
import { DownstreamSwipe } from '../Actions/DownstreamSwipe'
import { DownstreamTile } from '../Tiles/DownstreamTile'

type TViewType = 'mechanical' | 'process'

const processStatusFilter = new Set(['permit-needed', 'not-ready', 'ready'])

export const DownstreamTaskList = memo(function DownstreamTaskList() {
  const theme = useTheme()

  const { getWeekRange } = useDate()
  const { selectedDate, selectedWeekDay } = useSharedDates()
  const { weekStart, weekEnd } = getWeekRange(selectedDate)
  const location = useLocation()
  const name = location.pathname.split('/').filter(Boolean).pop() || ''
  const isProcessView = name === 'process'

  const {
    data: rawTasks,
    isLoading,
    isFetching,
    refetch,
    isPaused,
  } = useFindTasks({
    startDate: weekStart,
    endDate: weekEnd,
    view: name as TViewType,
  })

  const tasks = rawTasks ?? []
  type TTask = (typeof tasks)[number]

  const timeResults = useCallback(
    (task: TTask) =>
      isEqual(new Date(task.startTime).getDay(), selectedWeekDay.date.getDay()),
    [selectedWeekDay],
  )

  const dayTasks = tasks
    .filter(timeResults)
    .filter((task) =>
      isProcessView ? processStatusFilter.has(task.status) : true,
    )
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

  const renderItem = useCallback(
    (task: TTask) => (
      <DownstreamSwipe task={task}>
        <DownstreamTile task={task} />
      </DownstreamSwipe>
    ),
    [],
  )

  return (
    <>
      {isLoading ? (
        <LoaderContainer>
          <Loading color={theme.colors.text} size="small" />
        </LoaderContainer>
      ) : dayTasks.length > 0 ? (
        <div>
          {dayTasks.map((task, index) => (
            <div key={task.id.toString()}>
              {index > 0 && <Separator />}
              {renderItem(task)}
            </div>
          ))}
        </div>
      ) : (
        <LoaderContainer>
          <NoTasksText>Oops, no tasks available.</NoTasksText>
        </LoaderContainer>
      )}
    </>
  )
})
